import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { OfficeNetwork } from '../../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

const emptyForm = { officeName: '', ips: [''] as string[], publicIpCidrFallback: '', gpsLatitude: '', gpsLongitude: '', gpsRadiusMeters: '150', networkKind: 'DIRECTA' as 'DIRECTA' | 'VPN', validateGps: true, notes: '', providerAsn: '', providerName: '', facilities: '', address: '', neighborhood: '', commune: '', boundaries: '', landmark: '', city: '', country: '', mapSearchUrl: '', networkEquipment: '', ipIsFixed: '' as '' | 'SI' | 'NO' };

/**
 * Configuración > Redes de oficina: sedes autorizadas para clasificar un
 * marcaje como Presencial. SIN ningún campo de BSSID (descartado - una web
 * app no puede leerlo desde el navegador) - solo IP pública exacta (señal
 * primaria), geocerco GPS (respaldo) y un /24 opcional de baja confianza.
 */
export function OfficeNetworksTab() {
  const toast = useToast();
  const [rows, setRows] = useState<OfficeNetwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OfficeNetwork | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<OfficeNetwork | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listOfficeNetworks(true).then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm(emptyForm);
    setCreating(true);
  };
  const openEdit = (r: OfficeNetwork) => {
    setForm({
      officeName: r.officeName,
      ips: r.ips.length ? r.ips.map((i) => i.ipAddress) : [''],
      publicIpCidrFallback: r.publicIpCidrFallback ?? '',
      gpsLatitude: r.gpsLatitude != null ? String(r.gpsLatitude) : '',
      gpsLongitude: r.gpsLongitude != null ? String(r.gpsLongitude) : '',
      gpsRadiusMeters: String(r.gpsRadiusMeters),
      networkKind: r.networkKind,
      validateGps: r.validateGps,
      notes: r.notes ?? '',
      providerAsn: r.providerAsn != null ? String(r.providerAsn) : '',
      providerName: r.providerName ?? '',
      facilities: r.facilities ?? '',
      address: r.address ?? '',
      neighborhood: r.neighborhood ?? '',
      commune: r.commune ?? '',
      boundaries: r.boundaries ?? '',
      landmark: r.landmark ?? '',
      city: r.city ?? '',
      country: r.country ?? '',
      mapSearchUrl: r.mapSearchUrl ?? '',
      networkEquipment: r.networkEquipment ?? '',
      ipIsFixed: (r.ipIsFixed === true ? 'SI' : r.ipIsFixed === false ? 'NO' : '') as '' | 'SI' | 'NO',
    });
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };

  const save = async () => {
    const ips = form.ips.map((i) => i.trim()).filter(Boolean);
    if (!form.officeName.trim() || (ips.length === 0 && !form.providerAsn.trim())) {
      toast.show('Nombre de sede y al menos una IP o el ASN del proveedor son obligatorios.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        officeName: form.officeName,
        ips,
        publicIpCidrFallback: form.publicIpCidrFallback.trim() || null,
        gpsLatitude: form.gpsLatitude.trim() ? Number(form.gpsLatitude) : null,
        gpsLongitude: form.gpsLongitude.trim() ? Number(form.gpsLongitude) : null,
        gpsRadiusMeters: Number(form.gpsRadiusMeters) || 150,
        networkKind: form.networkKind,
        validateGps: form.validateGps,
        notes: form.notes.trim() || null,
        providerAsn: form.providerAsn.trim() ? Number(form.providerAsn) : null,
        providerName: form.providerName.trim() || null,
        facilities: form.facilities.trim() || null,
        address: form.address.trim() || null,
        neighborhood: form.neighborhood.trim() || null,
        commune: form.commune.trim() || null,
        boundaries: form.boundaries.trim() || null,
        landmark: form.landmark.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        mapSearchUrl: form.mapSearchUrl.trim() || null,
        networkEquipment: form.networkEquipment.trim() || null,
        ipIsFixed: form.ipIsFixed === 'SI' ? true : form.ipIsFixed === 'NO' ? false : null,
      };
      if (editing) {
        await repo.updateOfficeNetwork(editing.id, payload);
        toast.show('Sede actualizada.');
      } else {
        await repo.createOfficeNetwork(payload);
        toast.show('Sede agregada.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const detectMyIp = async (index: number) => {
    try {
      const n = await repo.getMyNetwork();
      if (!n.suggestedEntry) {
        toast.show('El servidor no pudo detectar tu IP.', 'error');
        return;
      }
      setForm((f) => {
        const ips = [...f.ips];
        ips[index] = n.suggestedEntry!;
        return { ...f, ips };
      });
      toast.show(`Detectada: ${n.ip} (se compara como ${n.comparedAs}).`);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo detectar la IP.', 'error');
    }
  };

  const toggleActive = async (r: OfficeNetwork) => {
    await repo.updateOfficeNetwork(r.id, { isActive: !r.isActive });
    load();
  };

  const columns: Column<OfficeNetwork>[] = [
    { key: 'officeName', filterValue: (r) => r.officeName, header: 'Sede', render: (r) => r.officeName, sortable: true, sortValue: (r) => r.officeName },
    { key: 'ip', header: 'IPs / redes', render: (r) => <span className="break-all">{r.ips.map((i) => i.ipAddress).join(', ') || '—'}</span> },
    { key: 'kind', filterValue: (r) => (r.networkKind === 'VPN' ? 'VPN corporativa' : 'Internet propio'), header: 'Tipo de red', render: (r) => <Badge tone={r.networkKind === 'VPN' ? 'warning' : 'info'}>{r.networkKind === 'VPN' ? 'VPN corporativa' : 'Internet propio'}</Badge> },
    { key: 'asn', filterValue: (r) => (r.providerAsn != null ? `AS${r.providerAsn}` : ''), header: 'Proveedor (ASN)', render: (r) => (r.providerAsn != null ? `${r.providerName ?? ''} AS${r.providerAsn}`.trim() : '—') },
    { key: 'place', filterValue: (r) => [r.address, r.neighborhood, r.commune, r.city].filter(Boolean).join(' '), header: 'Ubicación', render: (r) => (
      <span className="block min-w-[12rem]">
        {[r.address, r.neighborhood, r.commune, r.city, r.country].filter(Boolean).join(' · ') || '—'}
        {r.mapSearchUrl && <> <a href={r.mapSearchUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand-700 underline">Ver en mapa</a></>}
      </span>
    ) },
    { key: 'vgps', filterValue: (r) => (r.validateGps ? 'Sí' : 'No (solo IP)'), header: 'Valida GPS', render: (r) => (r.validateGps ? 'Sí' : 'No (solo IP)') },
    { key: 'cidr', header: '/24 respaldo', render: (r) => r.publicIpCidrFallback ?? '—' },
    { key: 'gps', header: 'Geocerco GPS', render: (r) => (r.gpsLatitude != null ? `${r.gpsLatitude.toFixed(5)}, ${r.gpsLongitude!.toFixed(5)} (${r.gpsRadiusMeters} m)` : '—') },
    {
      key: 'activo',
      header: 'Estado',
      render: (r) => (
        <button onClick={() => toggleActive(r)}>
          <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activa' : 'Inactiva'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar sede
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="📍" title="Sin sedes registradas" description="Agrega la primera para que TurnoGeo pueda clasificar marcajes como Presencial." />
      ) : (
        <DataTable excel fixedKey="officeName" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} emptyTitle="Sin sedes" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar sede' : 'Agregar sede'}>
        <div className="space-y-3">
          <label className="block text-sm font-medium text-ink-700">Tipo de red
            <select value={form.networkKind} onChange={(e) => setForm({ ...form, networkKind: e.target.value as 'DIRECTA' | 'VPN' })} className="mt-1 w-full rounded-xl border border-ink-200 bg-surface px-3 py-2.5 text-sm">
              <option value="DIRECTA">Internet propio de la sede (la IP identifica el lugar)</option>
              <option value="VPN">VPN corporativa (la IP solo prueba que está en la red de la empresa)</option>
            </select>
          </label>
          <label className="flex items-start gap-2 text-sm text-ink-700">
            <input type="checkbox" className="mt-1" checked={!form.validateGps} onChange={(e) => setForm({ ...form, validateGps: !e.target.checked })} />
            <span>En esta sede el GPS no funciona (ej. dentro del edificio): validar solo por IP.</span>
          </label>
          <Input label="Nombre de sede" placeholder="Ej. Bodega Principal" value={form.officeName} onChange={(e) => setForm({ ...form, officeName: e.target.value })} />
          <div className="space-y-2">
            <p className="text-sm font-medium text-ink-700">IPs o redes de la sede</p>
            {form.ips.map((ip, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label={`IP ${i + 1}`}
                    placeholder="190.85.12.34 o 2800:484:dd97:9a00::/64"
                    value={ip}
                    onChange={(e) => setForm({ ...form, ips: form.ips.map((v, j) => (j === i ? e.target.value : v)) })}
                  />
                </div>
                <Button variant="outline" size="md" onClick={() => detectMyIp(i)}>Detectar mi IP</Button>
                {form.ips.length > 1 && (
                  <Button variant="ghost" size="md" onClick={() => setForm({ ...form, ips: form.ips.filter((_, j) => j !== i) })}>✕</Button>
                )}
              </div>
            ))}
            <Button variant="ghost" size="md" onClick={() => setForm({ ...form, ips: [...form.ips, ''] })}>+ Agregar otra IP</Button>
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-ink-100 p-3">
              <Input label="ASN del proveedor (opcional)" placeholder="394089" value={form.providerAsn} onChange={(e) => setForm({ ...form, providerAsn: e.target.value.replace(/\D/g, '') })} />
              <Input label="Proveedor" placeholder="Palo Alto Networks" value={form.providerName} onChange={(e) => setForm({ ...form, providerName: e.target.value })} />
              <p className="col-span-2 text-xs text-ink-400">Si la IP cambia por usuario (VPN corporativa), la sede se reconoce por el proveedor. El ASN lo ves en Marcar o en el Registrador ("Detectar mi IP"). Un ASN solo puede identificar una sede.</p>
            </div>
            <p className="text-xs text-ink-400">"Detectar mi IP" muestra lo que ve el servidor desde tu conexión actual. En IPv6 se registra el prefijo de red (la parte final rota sola).</p>
          </div>
          <Input
            label="Rango /24 de respaldo (opcional, baja confianza)"
            placeholder="Ej. 190.85.12.0/24"
            value={form.publicIpCidrFallback}
            onChange={(e) => setForm({ ...form, publicIpCidrFallback: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Latitud (geocerco)" placeholder="4.710989" value={form.gpsLatitude} onChange={(e) => setForm({ ...form, gpsLatitude: e.target.value })} />
            <Input label="Longitud (geocerco)" placeholder="-74.072090" value={form.gpsLongitude} onChange={(e) => setForm({ ...form, gpsLongitude: e.target.value })} />
          </div>
          {form.networkKind === 'VPN' && <p className="rounded-xl bg-amber-50 p-3 text-xs text-warning">Con VPN, si registras coordenadas aproximadas de la sede, cuando el GPS sirva y quede a más de 2 km se registra una inconsistencia ("Oficina por VPN pero el GPS está lejos").</p>}
          <Input label="Radio del geocerco (metros)" type="number" value={form.gpsRadiusMeters} onChange={(e) => setForm({ ...form, gpsRadiusMeters: e.target.value })} />
          <details className="rounded-xl border border-ink-100 p-3" open={!!(form.address || form.neighborhood || form.facilities)}>
            <summary className="cursor-pointer text-sm font-semibold text-ink-700">Ficha de la sede (opcional, se va completando)</summary>
            <div className="mt-3 space-y-3">
              <Input label="Instalaciones" placeholder="Ej. Instalaciones de Socya" value={form.facilities} onChange={(e) => setForm({ ...form, facilities: e.target.value })} />
              <Input label="Dirección" placeholder="Ej. Calle 30 No. 55-198, Avenida Universidad de Medellín" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Barrio" value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
                <Input label="Comuna" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} />
                <Input label="Ciudad" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <Input label="País" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <Input label="Límites" placeholder="Ej. Limita con la Calle 30 al sur…" value={form.boundaries} onChange={(e) => setForm({ ...form, boundaries: e.target.value })} />
              <Input label="Punto de referencia" placeholder="Ej. Frente a la estación Nutibara" value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} />
              <Input label="Enlace de búsqueda en mapas" placeholder="https://www.google.com/maps?q=lat,lon (se arma solo con las coordenadas)" value={form.mapSearchUrl} onChange={(e) => setForm({ ...form, mapSearchUrl: e.target.value })} />
              <Input label="Equipo de red" placeholder="Ej. Palo Alto Networks (firewall/VPN)" value={form.networkEquipment} onChange={(e) => setForm({ ...form, networkEquipment: e.target.value })} />
              <label className="block text-sm font-medium text-ink-700">¿La IP pública de la sede es fija?
                <select value={form.ipIsFixed} onChange={(e) => setForm({ ...form, ipIsFixed: e.target.value as '' | 'SI' | 'NO' })} className="mt-1 w-full rounded-xl border border-ink-200 bg-surface px-3 py-2.5 text-sm">
                  <option value="">Aún no se sabe</option>
                  <option value="SI">Sí, es fija</option>
                  <option value="NO">No, cambia</option>
                </select>
              </label>
            </div>
          </details>
          <Input label="Notas (opcional)" placeholder="Ej. salida por Palo Alto; pedir a TI las IP fijas" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Desactivar sede"
        description={`¿Desactivar "${deleting?.officeName}"? Dejará de usarse para clasificar marcajes como Presencial.`}
        tone="danger"
        confirmLabel="Desactivar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteOfficeNetwork(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
